import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PortalService } from "services/portal";
import { getPortalSocket } from "services/portal/socket";
import { RefreshCw, CheckCircle, AlertCircle, Calendar, Loader2, ArrowUpRight } from "lucide-react";

// ─── Utils ────────────────────────────────────────────────────────────
const MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const fmtCLP = (n) => {
  if (n == null || isNaN(n)) return "—";
  return "$" + Math.round(n).toLocaleString("es-CL");
};

const fmtNum = (n, decimals = 0) => {
  if (n == null || isNaN(n)) return "—";
  return Number(n).toLocaleString("es-CL", { maximumFractionDigits: decimals });
};

function relativeTime(date) {
  if (!date) return "—";
  const diffMs = Date.now() - new Date(date).getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "hace un instante";
  if (min < 60) return `hace ${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `hace ${hr}h`;
  const days = Math.floor(hr / 24);
  return `hace ${days} día${days === 1 ? "" : "s"}`;
}

// ─── Componentes (todos inline-styles para coherencia con el resto del portal) ──
// Los colores son CSS vars que cambian con el theme. Ver assets/styles/index.css
// (raíz :root para light, [data-theme="dark"] para dark).
const COLORS = {
  bg: "var(--bg-page)",
  textPrimary: "var(--text-primary)",
  textSecondary: "var(--text-secondary)",
  textMuted: "var(--text-muted)",
  border: "var(--border)",
  borderLight: "var(--border-light)",
  cardBg: "var(--bg-card)",
  positive: "var(--positive)",
  positiveBg: "var(--positive-bg)",
  negative: "var(--negative)",
  negativeBg: "var(--negative-bg)",
  warning: "var(--warning)",
  warningBg: "var(--warning-bg)",
  info: "var(--info)",
  infoBg: "var(--info-bg)",
};

function IndicatorsTicker({ data, loading }) {
  if (loading) {
    return (
      <div style={{ background: COLORS.cardBg, borderBottom: `1px solid ${COLORS.border}`, padding: "10px 24px", fontSize: 12, color: COLORS.textMuted }}>
        Cargando indicadores…
      </div>
    );
  }
  if (!data) return null;

  const items = [
    { label: "UF", value: fmtCLP(data.uf) },
    { label: "UTM", value: fmtCLP(data.utm) },
    { label: "IMM", value: fmtCLP(data.imm) },
    { label: "IPC", value: data.ipc != null ? `${data.ipc > 0 ? "+" : ""}${data.ipc}%` : "—" },
  ];

  return (
    <div style={{
      background: COLORS.cardBg,
      borderBottom: `1px solid ${COLORS.border}`,
      padding: "12px 24px",
      display: "flex",
      gap: 32,
      alignItems: "center",
      fontSize: 13,
      flexWrap: "wrap",
    }}>
      {items.map((it, i) => (
        <div key={it.label} style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <span style={{ color: COLORS.textMuted, fontWeight: 500, fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase" }}>
            {it.label}
          </span>
          <span style={{ color: COLORS.textPrimary, fontWeight: 600, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
            {it.value}
          </span>
        </div>
      ))}
      {data.source === "DEFAULT" && (
        <span style={{ marginLeft: "auto", fontSize: 10, color: COLORS.textMuted, fontStyle: "italic" }}>
          valores por defecto · próxima actualización 06:00
        </span>
      )}
    </div>
  );
}

const ACCENT_MAP = {
  primary:  { fg: COLORS.info,     bg: COLORS.infoBg,     border: "var(--info-border)" },
  positive: { fg: COLORS.positive, bg: COLORS.positiveBg, border: "var(--positive-border)" },
  warning:  { fg: COLORS.warning,  bg: COLORS.warningBg,  border: "var(--warning-border)" },
  negative: { fg: COLORS.negative, bg: COLORS.negativeBg, border: "var(--negative-border)" },
};

function SalesCard({ label, total, count, neto, accent = "primary", noun = "factura", to }) {
  const accentSet = ACCENT_MAP[accent] || ACCENT_MAP.primary;
  const accentColor = accentSet.fg;
  const accentBg = accentSet.bg;
  const accentBorder = accentSet.border;
  const [hovered, setHovered] = useState(false);
  const clickable = Boolean(to);

  const content = (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 12 }}>
        <span style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: COLORS.textSecondary,
        }}>
          {label}
        </span>
        {clickable && (
          <ArrowUpRight
            size={16}
            color={hovered ? accentColor : COLORS.textMuted}
            style={{ transition: "color 120ms" }}
          />
        )}
      </div>
      <div style={{
        fontSize: 36,
        fontWeight: 700,
        color: COLORS.textPrimary,
        lineHeight: 1.1,
        letterSpacing: -0.5,
        marginBottom: 12,
      }}>
        {fmtCLP(total)}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{
          background: accentBg,
          color: accentColor,
          padding: "4px 10px",
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 600,
        }}>
          {count || 0} {count === 1 ? noun : noun + "s"}
        </span>
        {neto > 0 && (
          <span style={{ fontSize: 12, color: COLORS.textMuted }}>
            Neto {fmtCLP(neto)}
          </span>
        )}
      </div>
    </>
  );

  const cardStyle = {
    background: COLORS.cardBg,
    borderRadius: 16,
    border: `1px solid ${hovered && clickable ? accentBorder : COLORS.border}`,
    padding: "24px 28px",
    transition: "border-color 120ms, box-shadow 120ms, transform 120ms",
    cursor: clickable ? "pointer" : "default",
    boxShadow: hovered && clickable ? "0 4px 16px rgba(15,23,42,0.06)" : "none",
    transform: hovered && clickable ? "translateY(-1px)" : "none",
    textDecoration: "none",
    color: "inherit",
    display: "block",
  };

  if (clickable) {
    return (
      <Link
        to={to}
        style={cardStyle}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {content}
      </Link>
    );
  }

  return <div style={cardStyle}>{content}</div>;
}

function IvaCard({ data, loading }) {
  if (loading || !data) {
    return (
      <div style={{
        background: COLORS.cardBg,
        borderRadius: 16,
        border: `1px solid ${COLORS.border}`,
        padding: "24px 28px",
      }}>
        <span style={{ color: COLORS.textMuted, fontSize: 13 }}>Calculando IVA…</span>
      </div>
    );
  }

  const { ivaAPagar, ivaRemanente, debitoFiscal, creditoFiscal, dueDate, daysUntilDue, hasData } = data;
  const hasRemanente = ivaRemanente > 0;
  const monto = hasRemanente ? ivaRemanente : ivaAPagar;
  const label = hasRemanente ? "IVA a tu favor (remanente)" : "IVA a pagar este mes";

  let urgencyColor = COLORS.textMuted;
  let urgencyBg = "transparent";
  let urgencyText = "";
  if (!hasRemanente && hasData) {
    if (daysUntilDue < 0) {
      urgencyColor = COLORS.negative;
      urgencyBg = COLORS.negativeBg;
      urgencyText = `Vencido hace ${Math.abs(daysUntilDue)} días`;
    } else if (daysUntilDue <= 5) {
      urgencyColor = COLORS.negative;
      urgencyBg = COLORS.negativeBg;
      urgencyText = `Vence en ${daysUntilDue} día${daysUntilDue === 1 ? "" : "s"}`;
    } else if (daysUntilDue <= 15) {
      urgencyColor = COLORS.warning;
      urgencyBg = COLORS.warningBg;
      urgencyText = `Vence en ${daysUntilDue} días`;
    } else {
      urgencyColor = COLORS.textSecondary;
      urgencyText = `Vence en ${daysUntilDue} días`;
    }
  }

  const dueDateStr = dueDate ? new Date(dueDate).toLocaleDateString("es-CL", { day: "numeric", month: "long" }) : "—";

  return (
    <div style={{
      background: COLORS.cardBg,
      borderRadius: 16,
      border: `1px solid ${COLORS.border}`,
      padding: "24px 28px",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 16 }}>
        <span style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: COLORS.textSecondary,
        }}>
          {label}
        </span>
        {urgencyText && (
          <span style={{
            background: urgencyBg,
            color: urgencyColor,
            padding: "5px 12px",
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}>
            <Calendar size={12} />
            {urgencyText}
          </span>
        )}
      </div>

      <div style={{
        fontSize: 44,
        fontWeight: 700,
        color: hasRemanente ? COLORS.positive : COLORS.textPrimary,
        lineHeight: 1.1,
        letterSpacing: -0.5,
        marginBottom: 20,
      }}>
        {fmtCLP(monto)}
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 16,
        paddingTop: 16,
        borderTop: `1px solid ${COLORS.borderLight}`,
      }}>
        <div>
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 500 }}>
            Débito fiscal (ventas)
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.textPrimary, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
            {fmtCLP(debitoFiscal)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 500 }}>
            Crédito fiscal (compras)
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.textPrimary, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
            {fmtCLP(creditoFiscal)}
          </div>
        </div>
      </div>

      {!hasData && (
        <div style={{
          marginTop: 14,
          padding: "10px 14px",
          background: COLORS.warningBg,
          color: COLORS.warning,
          borderRadius: 8,
          fontSize: 12,
          lineHeight: 1.5,
        }}>
          Sin datos del SII para este período. Sincroniza tus compras y ventas para ver el IVA real.
        </div>
      )}

      {hasData && (
        <div style={{ marginTop: 14, fontSize: 12, color: COLORS.textMuted }}>
          Fecha límite de declaración: <strong style={{ color: COLORS.textSecondary }}>{dueDateStr}</strong>
        </div>
      )}
    </div>
  );
}

function SiiStatusLine({ configured, lastSyncAt }) {
  const dotColor = configured ? COLORS.positive : COLORS.negative;
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "12px 0",
      fontSize: 13,
      color: COLORS.textSecondary,
    }}>
      <span style={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        background: dotColor,
        flexShrink: 0,
      }} />
      <span>
        {configured ? "Credenciales SII configuradas" : "Credenciales SII no configuradas"}
      </span>
      {lastSyncAt && (
        <>
          <span style={{ color: COLORS.border }}>·</span>
          <span>Última sincronización {relativeTime(lastSyncAt)}</span>
        </>
      )}
      {!configured && (
        <a href="/portal/perfil" style={{ marginLeft: "auto", color: COLORS.info, textDecoration: "none", fontWeight: 500 }}>
          Configurar →
        </a>
      )}
    </div>
  );
}

function SyncBanner({ sync }) {
  if (!sync) return null;
  const icon = sync.stage === "error" ? AlertCircle : sync.stage === "done" ? CheckCircle : Loader2;
  const Icon = icon;
  const color = sync.stage === "error" ? COLORS.negative : sync.stage === "done" ? COLORS.positive : COLORS.info;
  const bg = sync.stage === "error" ? COLORS.negativeBg : sync.stage === "done" ? COLORS.positiveBg : COLORS.infoBg;
  const borderColor = sync.stage === "error" ? "var(--negative-border)" : sync.stage === "done" ? "var(--positive-border)" : "var(--info-border)";

  return (
    <div style={{
      background: bg,
      border: `1px solid ${borderColor}`,
      borderRadius: 10,
      padding: "12px 16px",
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 16,
      fontSize: 13,
      color: COLORS.textPrimary,
    }}>
      <Icon size={14} className={sync.stage !== "done" && sync.stage !== "error" ? "spin" : ""} color={color} />
      <span style={{ color: COLORS.textSecondary }}>{sync.message || sync.stage}</span>
    </div>
  );
}

// ─── Vista principal ──────────────────────────────────────────────────
export function PortalDashboard() {
  const [indicators, setIndicators] = useState(null);
  const [ventasMes, setVentasMes] = useState(null);
  const [ventasHoy, setVentasHoy] = useState(null);
  const [comprasMes, setComprasMes] = useState(null);
  const [comprasHoy, setComprasHoy] = useState(null);
  const [iva, setIva] = useState(null);
  const [member, setMember] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sync, setSync] = useState(null);

  const now = new Date();
  const period = { year: now.getFullYear(), month: now.getMonth() + 1 };

  const load = useCallback(async () => {
    try {
      const empty = { totalNeto: 0, totalIva: 0, totalMonto: 0, cantidadDocumentos: 0 };
      const [indResp, ventasMesResp, ventasHoyResp, comprasMesResp, comprasHoyResp, ivaResp, dashboardResp, historyResp] = await Promise.all([
        PortalService.getPortalIndicators().catch(() => null),
        PortalService.getVentas({ month: period.month, year: period.year }).catch(() => null),
        PortalService.getVentasHoy().catch(() => null),
        PortalService.getCompras({ month: period.month, year: period.year }).catch(() => null),
        PortalService.getComprasHoy().catch(() => null),
        PortalService.getIvaAPagar({ year: period.year, month: period.month }).catch(() => null),
        PortalService.getDashboard().catch(() => null),
        PortalService.getSyncHistory().catch(() => []),
      ]);
      setIndicators(indResp);
      setVentasMes(ventasMesResp?.summary || empty);
      setVentasHoy(ventasHoyResp?.summary || empty);
      setComprasMes(comprasMesResp?.summary || empty);
      setComprasHoy(comprasHoyResp?.summary || empty);
      setIva(ivaResp);
      setMember(dashboardResp?.member || null);
      const lastCompleted = Array.isArray(historyResp)
        ? historyResp.find((s) => s.status === "COMPLETED")
        : (historyResp?.syncs || []).find((s) => s.status === "COMPLETED");
      setLastSync(lastCompleted?.completedAt || null);
    } catch (err) {
      console.error("[Dashboard load]", err);
    }
  }, [period.year, period.month]);

  useEffect(() => {
    load().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Socket SII sync feedback
  useEffect(() => {
    const socket = getPortalSocket();
    if (!socket) return;
    const onStart = (p) => setSync({ stage: "start", message: "Iniciando sincronización con el SII…", ...p });
    const onProgress = (p) => setSync((prev) => ({ ...(prev || {}), ...p }));
    const onDone = (p) => {
      setSync({ stage: "done", message: `Sincronización completa · ${p?.result?.totalImported ?? 0} registros nuevos`, ...p });
      load();
      setTimeout(() => setSync(null), 5000);
    };
    const onError = (p) => {
      setSync({ stage: "error", message: p?.message || "Error en sincronización", ...p });
      setTimeout(() => setSync(null), 8000);
    };
    socket.on("sync:start", onStart);
    socket.on("sync:progress", onProgress);
    socket.on("sync:done", onDone);
    socket.on("sync:error", onError);
    return () => {
      socket.off("sync:start", onStart);
      socket.off("sync:progress", onProgress);
      socket.off("sync:done", onDone);
      socket.off("sync:error", onError);
    };
  }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh", color: COLORS.textMuted }}>
        Cargando tu dashboard…
      </div>
    );
  }

  return (
    <div style={{ background: COLORS.bg, minHeight: "100vh", margin: "-24px -32px" }}>
      <IndicatorsTicker data={indicators} loading={!indicators} />

      <div style={{ padding: "32px 32px 48px" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1 style={{
              fontSize: 28,
              fontWeight: 700,
              color: COLORS.textPrimary,
              marginBottom: 4,
              letterSpacing: -0.5,
            }}>
              Hola{member?.name ? `, ${member.name.split(" ")[0]}` : ""}
            </h1>
            <p style={{ fontSize: 14, color: COLORS.textSecondary }}>
              {member?.company_information?.name || "Tu empresa"} · {MONTHS[period.month - 1]} {period.year}
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 16px",
              background: COLORS.cardBg,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 500,
              color: COLORS.textSecondary,
              cursor: refreshing ? "not-allowed" : "pointer",
            }}
          >
            <RefreshCw size={13} className={refreshing ? "spin" : ""} />
            Actualizar
          </button>
        </div>

        <SyncBanner sync={sync} />

        {/* Ventas: 2 cards (click → /portal/ventas) */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
          <SalesCard
            label="Tus ventas este mes"
            total={ventasMes?.totalMonto}
            count={ventasMes?.cantidadDocumentos}
            neto={ventasMes?.totalNeto}
            accent="primary"
            to="/portal/ventas"
          />
          <SalesCard
            label="Tus ventas hoy"
            total={ventasHoy?.totalMonto}
            count={ventasHoy?.cantidadDocumentos}
            neto={ventasHoy?.totalNeto}
            accent="positive"
            to="/portal/ventas"
          />
        </div>

        {/* Compras: 2 cards (click → /portal/compras) */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
          <SalesCard
            label="Tus compras este mes"
            total={comprasMes?.totalMonto}
            count={comprasMes?.cantidadDocumentos}
            neto={comprasMes?.totalNeto}
            accent="warning"
            to="/portal/compras"
          />
          <SalesCard
            label="Tus compras hoy"
            total={comprasHoy?.totalMonto}
            count={comprasHoy?.cantidadDocumentos}
            neto={comprasHoy?.totalNeto}
            accent="warning"
            to="/portal/compras"
          />
        </div>

        {/* IVA */}
        <div style={{ marginBottom: 24 }}>
          <IvaCard data={iva} loading={!iva} />
        </div>

        {/* Estado SII */}
        <SiiStatusLine
          configured={member?.siiConfigured}
          lastSyncAt={lastSync}
        />
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}
